import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './BroadcastsList.css';

const BroadcastsList = () => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state for adding/editing broadcasts
  const [formData, setFormData] = useState({
    title: '',
    type: 'Announcement',
    content: '',
    datetime: ''
  });

  // Fetch broadcasts on component mount
  useEffect(() => {
    fetchBroadcasts();
  }, []);

  // Fetch broadcasts with optional search
  const fetchBroadcasts = async (search = '') => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getBroadcasts(search);
      console.log('Broadcasts response:', response); // Debug log
      setBroadcasts(Array.isArray(response) ? response : []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching broadcasts:', err);
      setBroadcasts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchBroadcasts(searchQuery);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle add broadcast
  const handleAddBroadcast = async (e) => {
    e.preventDefault();
    try {
      const broadcastData = {
        ...formData,
        datetime: formData.datetime || new Date().toISOString()
      };
      await apiService.addBroadcast(broadcastData);
      setFormData({
        title: '',
        type: 'Announcement',
        content: '',
        datetime: ''
      });
      setShowAddForm(false);
      fetchBroadcasts(searchQuery);
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle edit broadcast
  const handleEditBroadcast = async (e) => {
    e.preventDefault();
    try {
      await apiService.updateBroadcast(editingBroadcast, formData);
      setEditingBroadcast(null);
      setFormData({
        title: '',
        type: 'Announcement',
        content: '',
        datetime: ''
      });
      fetchBroadcasts(searchQuery);
    } catch (err) {
      setError(err.message);
    }
  };

  // Start editing a broadcast
  const startEdit = (broadcast) => {
    setEditingBroadcast(broadcast.ROWID);
    setFormData({
      title: broadcast.title || '',
      type: broadcast.type || 'Announcement',
      content: broadcast.content || '',
      datetime: broadcast.datetime || broadcast.date || ''
    });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingBroadcast(null);
    setFormData({
      title: '',
      type: 'Announcement',
      content: '',
      datetime: ''
    });
  };

  // Handle delete broadcast
  const handleDeleteBroadcast = async (rowId) => {
    if (window.confirm('Are you sure you want to delete this broadcast?')) {
      try {
        await apiService.deleteBroadcast(rowId);
        fetchBroadcasts(searchQuery);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <div className="loading">Loading broadcasts...</div>;
  }

  return (
    <div className="broadcasts-container">
      <div className="broadcasts-header">
        <h2>Broadcasts Management</h2>
        
        {/* Search Form */}
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search broadcasts..."
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
          <button 
            type="button" 
            onClick={() => {
              setSearchQuery('');
              fetchBroadcasts();
            }}
            className="clear-btn"
          >
            Clear
          </button>
        </form>

        {/* Test API Button */}
        <button 
          onClick={async () => {
            try {
              console.log('Testing API connection...');
              const healthResponse = await apiService.healthCheck();
              console.log('Health check response:', healthResponse);
              alert('API is working! Check console for details.');
            } catch (error) {
              console.error('Health check failed:', error);
              alert(`API Error: ${error.message}`);
            }
          }}
          className="test-btn"
        >
          Test API
        </button>

        {/* Add Broadcast Button */}
        <button 
          onClick={() => setShowAddForm(true)}
          className="add-btn"
        >
          Add New Broadcast
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {(showAddForm || editingBroadcast) && (
        <div className="form-modal">
          <div className="form-content">
            <h3>{editingBroadcast ? 'Edit Broadcast' : 'Add New Broadcast'}</h3>
            <form onSubmit={editingBroadcast ? handleEditBroadcast : handleAddBroadcast}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Broadcast title"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="Announcement">Announcement</option>
                  <option value="Update">Update</option>
                  <option value="News">News</option>
                  <option value="Alert">Alert</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Content</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="Broadcast content"
                  rows="4"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Date & Time</label>
                <input
                  type="datetime-local"
                  name="datetime"
                  value={formData.datetime}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="save-btn">
                  {editingBroadcast ? 'Update' : 'Add'} Broadcast
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddForm(false);
                    cancelEdit();
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Broadcasts List */}
      <div className="broadcasts-list">
        {broadcasts.length === 0 ? (
          <div className="no-data">No broadcasts found</div>
        ) : (
          <div className="broadcasts-grid">
            {broadcasts.map((broadcast) => (
              <div key={broadcast.ROWID || broadcast.id} className="broadcast-card">
                <div className="broadcast-header">
                  <div className="broadcast-title-section">
                    <h4>{broadcast.title || 'Untitled'}</h4>
                    <span className={`type-badge ${broadcast.type?.toLowerCase()}`}>
                      {broadcast.type || 'Announcement'}
                    </span>
                  </div>
                  <div className="broadcast-actions">
                    <button 
                      onClick={() => startEdit(broadcast)}
                      className="edit-btn"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteBroadcast(broadcast.ROWID || broadcast.id)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="broadcast-content">
                  <p>{broadcast.content || 'No content'}</p>
                </div>
                
                <div className="broadcast-footer">
                  <div className="broadcast-date">
                    <strong>Date:</strong> {formatDate(broadcast.datetime || broadcast.date)}
                  </div>
                  <div className="broadcast-id">
                    <strong>ID:</strong> {broadcast.ROWID || broadcast.id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BroadcastsList;
